import {
  AdminStatusDto,
  CandidateItemDto,
  type CandidateSortRequestDto,
  GetCandidatesSortPropertyDto,
  SortDirectionDto,
} from '@/api/generated';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { initials, parseUtc, timeLeft } from '@/views/admin/candidates/candidateStatus.ts';
import { StatusChip } from '@/views/admin/candidates/StatusChip.comp.tsx';
import { COLORS, formatNumber, SCORE_COLORS } from '@/views/admin/dashboard/dashboardTheme.ts';
import React from 'react';
import { IoArrowDown, IoArrowUp, IoPauseCircle, IoTimeOutline } from 'react-icons/io5';

const MAX_POINTS = 850;
const SHOWN_AREAS = 2;

const isOnHold = (candidate: CandidateItemDto) =>
  candidate.adminStatus === AdminStatusDto.OnHoldAdmin ||
  candidate.adminStatus === AdminStatusDto.OnHoldConditional;

interface Column {
  key: string;
  label: string;
  sort?: GetCandidatesSortPropertyDto;
  width: string;
}

interface Props {
  candidates: CandidateItemDto[];
  isLoading: boolean;
  language: string;
  now: Date;
  onOpen: (candidate: CandidateItemDto) => void;
  onSort: (sort: CandidateSortRequestDto) => void;
  selectedId?: string;
  sort: CandidateSortRequestDto;
}

export const CandidatesTable: React.FC<Props> = ({
  candidates,
  isLoading,
  language,
  now,
  onOpen,
  onSort,
  selectedId,
  sort,
}) => {
  const { t } = useTranslationWithPrefix('views.admin');
  const dateTime = new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' });
  const columns: Column[] = [
    {
      key: 'candidate',
      label: t('candidates.columns.candidate'),
      sort: GetCandidatesSortPropertyDto.Candidate,
      width: '22%',
    },
    {
      key: 'status',
      label: t('candidates.columns.status'),
      sort: GetCandidatesSortPropertyDto.Status,
      width: '15%',
    },
    {
      key: 'score',
      label: t('candidates.columns.score'),
      sort: GetCandidatesSortPropertyDto.QuizPoints,
      width: '13%',
    },
    { key: 'studies', label: t('candidates.columns.studies'), width: '18%' },
    { key: 'areas', label: t('candidates.columns.areas'), width: '14%' },
    {
      key: 'quizFinished',
      label: t('candidates.columns.quizFinished'),
      sort: GetCandidatesSortPropertyDto.QuizFinishedAt,
      width: '9%',
    },
    {
      key: 'decisionDue',
      label: t('candidates.columns.decisionDue'),
      sort: GetCandidatesSortPropertyDto.AdminActionPossibleBefore,
      width: '9%',
    },
  ];

  const handleSort = (property: GetCandidatesSortPropertyDto) => () =>
    onSort({
      direction:
        sort.property === property && sort.direction === SortDirectionDto.Desc
          ? SortDirectionDto.Asc
          : SortDirectionDto.Desc,
      property,
    });

  return (
    <div
      className="overflow-x-auto"
      style={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 200ms' }}
    >
      <table
        className="w-full"
        style={{ borderCollapse: 'collapse', minWidth: 1000, tableLayout: 'fixed' }}
      >
        <colgroup>
          {columns.map(column => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead>
          <tr style={{ borderBottom: `1px solid ${COLORS.grid}` }}>
            {columns.map(column => {
              const active = column.sort && sort.property === column.sort;
              return (
                <th
                  aria-sort={
                    active
                      ? sort.direction === SortDirectionDto.Asc
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider"
                  key={column.key}
                  scope="col"
                  style={{ color: active ? COLORS.ink : COLORS.inkMuted }}
                >
                  {column.sort ? (
                    <button
                      aria-label={t('candidates.sortBy', { column: column.label })}
                      className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-xs font-bold uppercase tracking-wider"
                      onClick={handleSort(column.sort)}
                      style={{ color: 'inherit' }}
                      type="button"
                    >
                      {column.label}
                      {active &&
                        (sort.direction === SortDirectionDto.Asc ? (
                          <IoArrowUp aria-hidden />
                        ) : (
                          <IoArrowDown aria-hidden />
                        ))}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {candidates.map(candidate => {
            const deadline = parseUtc(candidate.adminActionPossibleBefore);
            const left = deadline ? timeLeft(deadline, now) : undefined;
            const quizFinished = parseUtc(candidate.quizFinishedAt);
            const selected = candidate.candidateId === selectedId;
            return (
              <tr
                aria-selected={selected}
                className="cursor-pointer transition-colors hover:bg-[#fafafa]"
                key={candidate.candidateId}
                onClick={() => onOpen(candidate)}
                onKeyDown={event => event.key === 'Enter' && onOpen(candidate)}
                style={{
                  backgroundColor: selected ? '#fff6ee' : undefined,
                  borderBottom: `1px solid ${COLORS.grid}`,
                  boxShadow: selected ? `inset 3px 0 0 ${COLORS.orange}` : undefined,
                }}
                tabIndex={0}
              >
                <td className="px-3 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      aria-hidden
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: COLORS.ink }}
                    >
                      {initials(candidate.firstName, candidate.lastName)}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-bold" style={{ color: COLORS.ink }}>
                        {candidate.lastName ? `${candidate.firstName} ${candidate.lastName}` : '—'}
                      </div>
                      <div className="truncate text-xs" style={{ color: COLORS.inkMuted }}>
                        {candidate.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <StatusChip status={candidate.status} />
                  {isOnHold(candidate) && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 text-xs"
                      style={{ color: '#8a3c00' }}
                    >
                      <IoPauseCircle aria-hidden />
                      {t(
                        `filters.adminStatuses.${candidate.adminStatus as Exclude<AdminStatusDto, 'NONE'>}`,
                      )}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  {candidate.quizPoints ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <strong style={{ color: COLORS.ink }}>
                          {formatNumber(candidate.quizPoints, language)}
                        </strong>
                        <span className="text-xs" style={{ color: COLORS.inkSecondary }}>
                          {t(`table.quizScores.${candidate.quizScore}`)}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full" style={{ backgroundColor: COLORS.grid }}>
                        <div
                          className="h-full"
                          style={{
                            backgroundColor: SCORE_COLORS[candidate.quizScore],
                            width: `${Math.min(100, (candidate.quizPoints / MAX_POINTS) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <span style={{ color: COLORS.inkMuted }}>—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-sm">
                  <div
                    className="truncate"
                    style={{ color: COLORS.ink }}
                    title={candidate.university}
                  >
                    {candidate.university ?? '—'}
                  </div>
                  <div className="truncate text-xs" style={{ color: COLORS.inkMuted }}>
                    {candidate.location ?? ''}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1">
                    {candidate.areasOfInterests.slice(0, SHOWN_AREAS).map(area => (
                      <span
                        className="max-w-full truncate px-1.5 py-0.5 text-xs"
                        key={area}
                        style={{
                          backgroundColor: '#f4f4f4',
                          borderRadius: 4,
                          color: COLORS.inkSecondary,
                        }}
                        title={area}
                      >
                        {area}
                      </span>
                    ))}
                    {candidate.areasOfInterests.length > SHOWN_AREAS && (
                      <span
                        className="px-1 py-0.5 text-xs"
                        style={{ color: COLORS.inkMuted }}
                        title={candidate.areasOfInterests.slice(SHOWN_AREAS).join(', ')}
                      >
                        +{candidate.areasOfInterests.length - SHOWN_AREAS}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-xs" style={{ color: COLORS.inkSecondary }}>
                  {quizFinished ? dateTime.format(quizFinished) : '—'}
                </td>
                <td className="px-3 py-3 text-xs">
                  {deadline ? (
                    left && isOnHold(candidate) ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 font-bold"
                        style={{ backgroundColor: '#fff1e3', borderRadius: 4, color: '#8a3c00' }}
                        title={dateTime.format(deadline)}
                      >
                        <IoTimeOutline aria-hidden />
                        {t('candidates.timeLeft', { time: left })}
                      </span>
                    ) : (
                      <span
                        style={{ color: COLORS.inkSecondary }}
                        title={dateTime.format(deadline)}
                      >
                        {left ? dateTime.format(deadline) : t('candidates.overdue')}
                      </span>
                    )
                  ) : (
                    <span style={{ color: COLORS.inkMuted }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!isLoading && candidates.length === 0 && (
        <p className="m-0 py-10 text-center" style={{ color: COLORS.inkMuted }}>
          {t('candidates.noResults')}
        </p>
      )}
    </div>
  );
};
